import { Button } from "@/components/atoms/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/atoms/Dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/molecules/Table";
import { cn } from "@/lib/utils";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { useMemo } from "react";

type Alert = {
  type: "COPY" | "SCREENSHOT";
  details: string;
  createdAt: string;
  copiedText?: string;
};

const columnHelper = createColumnHelper<Alert>();

interface AlertsSummaryDialogProps {
  alerts?: Alert[];
}

const AlertsSummaryDialog = ({ alerts }: AlertsSummaryDialogProps) => {
  const columns = useMemo(
    () => [
      columnHelper.accessor((x) => x, {
        id: "index",
        header: "N°",
        cell: ({ row }) => row.index + 1,
      }),
      columnHelper.accessor("type", {
        id: "type",
        header: "Type",
        cell: (info) => <span>{info.getValue().toLowerCase()}</span>,
      }),
      columnHelper.accessor("details", {
        id: "details",
        header: "Détails",
        cell: (info) => <span className="max-w-md">{info.getValue()}</span>,
      }),
      columnHelper.accessor("createdAt", {
        id: "timestamp",
        header: "Heure",
        cell: (info) => (
          <span>
            {format(new Date(info.getValue()), "HH:mm:ss", { locale: fr })}
          </span>
        ),
      }),
      columnHelper.accessor("copiedText", {
        id: "copiedText",
        header: "Texte copié",
        cell: (info) => {
          const text = info.getValue();
          return (
            <span className="break-words">
              {text ? `"${text}"` : "-"}
            </span>
          );
        },
      }),
    ],
    []
  );

  const table = useReactTable({
    data: alerts || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <Dialog>
      <DialogTrigger onClick={(e) => e.stopPropagation()} asChild>
        <Button
          variant="red"
          className="min-w-11"
          onClick={(e) => e.stopPropagation()}
        >
          {alerts?.length ?? "0"}
        </Button>
      </DialogTrigger>
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        className="max-h-[95%] overflow-auto xl:w-[1200px] xl:max-w-[95vw]"
      >
        <DialogHeader>
          <DialogTitle>Alertes de copie / capture d'écran</DialogTitle>
          <DialogDescription hidden>
            Alertes de copie / capture d'écran détectées
          </DialogDescription>
        </DialogHeader>
        <Table className="text-gray-600 font-medium text-sm leading-6">
          <TableHeader className="items-center bg-gray-50">
            <TableRow>
              {table.getHeaderGroups()[0].headers.map((header) => (
                <TableHead
                  key={header.id}
                  className="whitespace-nowrap"
                >
                  <div className="flex flex-row items-center gap-3 text-xs">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </div>
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.map((row, i) => (
              <TableRow
                key={row.id}
                className={cn({
                  "bg-gray-50": i % 2,
                })}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="h-14 py-0">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
    </Dialog>
  );
};

export default AlertsSummaryDialog;
